<template>
  <!--
    Экран объединяет правила публикации рейтинга и информационные тексты,
    которые видит абитуриент на внешних страницах.
  -->
  <v-card class="other-settings" elevation="0">
    <v-card-title class="other-settings__header">
      <div>
        <h2 class="other-settings__title">Другие настройки</h2>
        <p class="other-settings__description">
          Дополнительные тексты, правила отображения рейтингов и параметры заявления.
        </p>
      </div>

      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-content-save-outline"
        :loading="saving"
        :disabled="loading || !hasChanges"
        @click="saveSettings"
      >
        Сохранить
      </v-btn>
    </v-card-title>

    <v-card-text class="other-settings__content">
      <v-alert
        v-if="error"
        class="mb-5"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="successMessage"
        class="mb-5"
        type="success"
        variant="tonal"
        closable
        @click:close="successMessage = ''"
      >
        {{ successMessage }}
      </v-alert>

      <v-skeleton-loader
        v-if="loading"
        type="heading, paragraph, paragraph, article, article"
      />

      <div
        v-else
        class="other-settings__sections"
        @keydown.ctrl.enter.prevent="saveSettings"
      >
        <section class="other-settings__section">
          <div class="other-settings__section-heading">
            <div class="other-settings__section-icon">
              <v-icon icon="mdi-message-text-outline" size="23" />
            </div>
            <div>
              <h3>Информация для абитуриентов</h3>
              <p>Эти тексты отображаются на публичных страницах приемной комиссии.</p>
            </div>
          </div>

          <div class="other-settings__fields">
            <div class="other-settings__editor-field">
              <div class="other-settings__editor-label">
                Дополнительная информация в рейтинге
              </div>
              <div class="other-settings__editor">
                <ckeditor
                  v-model="settings.additionalInfoRating"
                  :editor="editor"
                  :config="editorConfig"
                  :disabled="saving"
                />
              </div>
              <div class="other-settings__editor-hint">
                Отображается над таблицей рейтинга.
              </div>
            </div>

            <div class="other-settings__editor-field">
              <div class="other-settings__editor-label">
                Дополнительная информация в заявлении(отображается на форме с паролем для доступа к анкете, после сохранения абитуриентом заявления)
              </div>
              <div class="other-settings__editor">
                <ckeditor
                  v-model="settings.additionalInfoForApplicant"
                  :editor="editor"
                  :config="editorConfig"
                  :disabled="saving"
                />
              </div>
              <div class="other-settings__editor-hint">
                Отображается абитуриенту при заполнении заявления.
              </div>
            </div>
          </div>
        </section>

        <section class="other-settings__section">
          <div class="other-settings__section-heading">
            <div class="other-settings__section-icon">
              <v-icon icon="mdi-comment-check-outline" size="23" />
            </div>
            <div>
              <h3>Рекомендации в рейтинге</h3>
              <p>
                Текст примечания выбирается автоматически по положению абитуриента
                в конкурсе.
              </p>
            </div>
          </div>

          <div class="other-settings__recommendations">
            <v-textarea
              v-model="settings.recomendation2"
              label="Проходит, оригинал предоставлен"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details="auto"
            />
            <v-textarea
              v-model="settings.recomendation1"
              label="Проходит, оригинал не предоставлен"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details="auto"
            />
            <v-textarea
              v-model="settings.recomendation3"
              label="Резерв"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details="auto"
            />
            <v-textarea
              v-model="settings.recomendation"
              label="Не проходит на бюджет, но есть договорные места"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details="auto"
            />
            <v-textarea
              v-model="settings.recomendation4"
              class="other-settings__recommendation-wide"
              label="Не проходит по конкурсу"
              variant="outlined"
              rows="2"
              auto-grow
              hide-details="auto"
            />
          </div>
        </section>

        <section class="other-settings__section">
          <div class="other-settings__section-heading">
            <div class="other-settings__section-icon">
              <v-icon icon="mdi-eye-settings-outline" size="23" />
            </div>
            <div>
              <h3>Отображение рейтингов</h3>
              <p>Настройте состав, сортировку и доступность рейтингов.</p>
            </div>
          </div>

          <div class="other-settings__switches">
            <article
              v-for="item in displaySettings"
              :key="item.key"
              class="other-settings__switch"
            >
              <div>
                <strong>{{ item.title }}</strong>
                <span>{{ item.description }}</span>
              </div>
              <v-switch
                v-model="settings[item.key]"
                color="primary"
                inset
                hide-details
                :aria-label="item.title"
              />
            </article>
          </div>
        </section>

        <section class="other-settings__section">
          <div class="other-settings__section-heading">
            <div class="other-settings__section-icon">
              <v-icon icon="mdi-format-list-numbered" size="23" />
            </div>
            <div>
              <h3>Параметры заявления</h3>
              <p>Общие ограничения при выборе конкурсных групп.</p>
            </div>
          </div>

          <v-text-field
            v-model="settings.countPriority"
            class="other-settings__priority"
            type="number"
            min="1"
            max="20"
            step="1"
            label="Количество приоритетов"
            hint="Допустимое значение — от 1 до 20"
            persistent-hint
            variant="outlined"
          />
        </section>

        <div class="other-settings__footer">
          <span v-if="hasChanges">Есть несохраненные изменения</span>
          <span v-else>Все изменения сохранены</span>
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-content-save-outline"
            :loading="saving"
            :disabled="!hasChanges"
            @click="saveSettings"
          >
            Сохранить настройки
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  AutoLink,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  GeneralHtmlSupport,
  Heading,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  SourceEditing,
  Table,
  TableToolbar,
  Underline,
  Undo
} from 'ckeditor5';
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import 'ckeditor5/ckeditor5.css';

export default {
  name: 'OtherSettingsSection',
  components: {
    Ckeditor
  },
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          AccessibilityHelp,
          Autoformat,
          AutoLink,
          Bold,
          Essentials,
          FontBackgroundColor,
          FontColor,
          GeneralHtmlSupport,
          Heading,
          Italic,
          Link,
          List,
          Paragraph,
          PasteFromOffice,
          SelectAll,
          SourceEditing,
          Table,
          TableToolbar,
          Underline,
          Undo
        ],
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'sourceEditing',
            '|',
            'heading',
            '|',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            'link',
            '|',
            'bulletedList',
            'numberedList',
            'insertTable',
            '|',
            'selectAll',
            'accessibilityHelp'
          ],
          shouldNotGroupWhenFull: false
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        htmlSupport: {
          allow: [
            {
              name: /^(p|div|span|h[1-6]|ul|ol|li|blockquote|table|thead|tbody|tfoot|tr|th|td|a|strong|em|u|s|br)$/,
              styles: true,
              classes: true,
              attributes: true
            }
          ]
        }
      },
      settings: {
        additionalInfoRating: '',
        recomendation: '',
        recomendation1: '',
        recomendation2: '',
        recomendation3: '',
        recomendation4: '',
        additionalInfoForApplicant: '',
        shortFIO: false,
        showNumsAnyway: false,
        docExistsSorting: false,
        showStudentsFirst: false,
        showUnenrolled: false,
        showRatings: false,
        showRatingsInGroup: false,
        showTargetOverall: false,
        hideRatingStepsOnMobile: false,
        countPriority: 3
      },
      initialSettings: '',
      loading: true,
      saving: false,
      error: '',
      successMessage: '',
      displaySettings: [
        {
          key: 'shortFIO',
          title: 'Сокращать имя и отчество',
          description: 'Показывать в рейтинге инициалы вместо полного имени и отчества.'
        },
        {
          key: 'showNumsAnyway',
          title: 'Нумеровать всех абитуриентов',
          description: 'Показывать порядковый номер даже без оригинала документа.'
        },
        {
          key: 'docExistsSorting',
          title: 'Абитуриенты без оригиналов в конце',
          description: 'Учитывать наличие оригинала при сортировке рейтинга.'
        },
        {
          key: 'showStudentsFirst',
          title: 'Зачисленные в начале рейтинга',
          description: 'Зачисленные абитуриенты получают преимущество при сортировке.'
        },
        {
          key: 'showUnenrolled',
          title: 'Показывать отчисленных',
          description: 'Не скрывать ранее зачисленных, а затем отчисленных абитуриентов.'
        },
        {
          key: 'showRatings',
          title: 'Рейтинги только после авторизации',
          description: 'Запретить гостевой просмотр всех рейтингов.'
        },
        {
          key: 'showRatingsInGroup',
          title: 'Рейтинги на пополнение только после авторизации',
          description: 'Ограничить гостевой доступ только к конкурсам на пополнение.'
        },
        {
          key: 'showTargetOverall',
          title: 'Целевой набор в общем рейтинге',
          description: 'Не выделять целевых абитуриентов в отдельную таблицу.'
        },
        {
          key: 'hideRatingStepsOnMobile',
          title: 'Отключать отображение этапов в мобильной версии',
          description: 'Скрывать столбцы этапов рейтинга на экранах шириной до 600 пикселей.'
        }
      ]
    };
  },
  computed: {
    hasChanges() {
      return !this.loading &&
        JSON.stringify(this.settings) !== this.initialSettings;
    }
  },
  created() {
    this.loadSettings();
  },
  methods: {
    emptySettings() {
      return {
        additionalInfoRating: '',
        recomendation: '',
        recomendation1: '',
        recomendation2: '',
        recomendation3: '',
        recomendation4: '',
        additionalInfoForApplicant: '',
        shortFIO: false,
        showNumsAnyway: false,
        docExistsSorting: false,
        showStudentsFirst: false,
        showUnenrolled: false,
        showRatings: false,
        showRatingsInGroup: false,
        showTargetOverall: false,
        hideRatingStepsOnMobile: false,
        countPriority: 3
      };
    },
    loadSettings() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getOtherAdmissionSettings'
        )
        .then((response) => {
          this.applySettings(response.data.settings || {});
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось загрузить другие настройки приемной комиссии.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    applySettings(settings) {
      // Снимок нужен для общей кнопки сохранения: в отличие от обязательных
      // полей изменения этого экрана применяются одним пакетом.
      this.settings = {
        ...this.emptySettings(),
        ...settings
      };
      this.initialSettings = JSON.stringify(this.settings);
    },
    saveSettings() {
      if (this.loading || this.saving || !this.hasChanges) {
        return;
      }

      const countPriority = Number(this.settings.countPriority);
      if (
        !Number.isInteger(countPriority) ||
        countPriority < 1 ||
        countPriority > 20
      ) {
        this.error = 'Количество приоритетов должно быть целым числом от 1 до 20.';
        return;
      }

      this.saving = true;
      this.error = '';
      this.successMessage = '';
      const submittedSettings = {
        ...this.settings,
        countPriority
      };

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveOtherAdmissionSettings',
          new URLSearchParams({
            settings: JSON.stringify(submittedSettings)
          })
        )
        .then((response) => {
          this.applySettings(response.data.settings || submittedSettings);
          this.successMessage = 'Другие настройки сохранены.';
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось сохранить другие настройки приемной комиссии.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response
        ? error.response.data
        : null;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
      if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
      }

      return fallback;
    }
  }
};
</script>

<style scoped>
.other-settings {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.other-settings__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px 14px;
  white-space: normal;
}

.other-settings__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.other-settings__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.other-settings__content {
  padding: 10px 28px 28px;
}

.other-settings__sections {
  display: grid;
  gap: 22px;
}

.other-settings__section {
  padding: 20px;
  border: 1px solid #e4e9f0;
  border-radius: 12px;
  background: #fff;
}

.other-settings__section-heading {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
}

.other-settings__section-icon {
  display: grid;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 10px;
  background: #eaf3fc;
  color: #1565c0;
}

.other-settings__section-heading h3 {
  margin: 0;
  color: #1d2939;
  font-size: 17px;
}

.other-settings__section-heading p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 13px;
}

.other-settings__fields {
  display: grid;
  gap: 24px;
}

.other-settings__editor-field {
  display: grid;
  gap: 7px;
}

.other-settings__editor-label {
  color: #344054;
  font-size: 14px;
  font-weight: 600;
}

.other-settings__editor {
  overflow: hidden;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

.other-settings__editor :deep(.ck.ck-editor__main > .ck-editor__editable) {
  min-height: 220px;
  max-height: 55vh;
  padding: 16px 20px;
  overflow-y: auto;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  box-shadow: none;
}

.other-settings__editor :deep(.ck.ck-toolbar) {
  border: 0;
  border-bottom: 1px solid #d0d5dd;
}

.other-settings__editor-hint {
  padding-left: 16px;
  color: #667085;
  font-size: 12px;
}

.other-settings__recommendations {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.other-settings__recommendation-wide {
  grid-column: 1 / -1;
}

.other-settings__switches {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.other-settings__switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 82px;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #e4e9f0;
  border-radius: 10px;
  background: #f9fafb;
}

.other-settings__switch > div {
  display: grid;
  gap: 4px;
}

.other-settings__switch strong {
  color: #344054;
  font-size: 14px;
}

.other-settings__switch span {
  color: #667085;
  font-size: 12px;
  line-height: 1.4;
}

.other-settings__priority {
  max-width: 320px;
}

.other-settings__footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 18px;
}

.other-settings__footer > span {
  color: #667085;
  font-size: 13px;
}

@media (max-width: 760px) {
  .other-settings__recommendations,
  .other-settings__switches {
    grid-template-columns: 1fr;
  }

  .other-settings__recommendation-wide {
    grid-column: auto;
  }

  .other-settings__header,
  .other-settings__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .other-settings__footer {
    text-align: right;
  }
}

@media (max-width: 600px) {
  .other-settings__header,
  .other-settings__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .other-settings__section {
    padding: 16px;
  }
}
</style>
